import {
  Badge,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  cn,
} from '@design-system';
import { SlideOutSheet, PageLink, CarePhaseDataItem } from '@/components';

import { Clock4, DollarSign, MapPinIcon, CircleQuestionMark } from 'lucide-react';

import { motion } from 'framer-motion';
import { getCareSettingsPageBy } from '@/util/navigationLogic';
import { formatCurrency, careEnvironmentDefs, CarePhase, carePhaseDefs } from '@shared';

export interface VerticalPhaseCardProps {
  phase: CarePhaseDataItem;
  phaseNumber: number;
  isLastPhase: boolean;
  isVisible: boolean;
  isLineVisible: boolean;
  carePhase: CarePhase;
  clientId: string;
  clientHasStartedLtc: boolean;
  clientCurrentCarePhase: CarePhase | null;
  timelineText: string;
  timelineTooltip: string;
}

export function VerticalPhaseCard({
  phase,
  phaseNumber,
  isLastPhase,
  isVisible,
  isLineVisible,
  carePhase,
  clientId,
  clientHasStartedLtc,
  clientCurrentCarePhase,
  timelineText,
  timelineTooltip,
}: VerticalPhaseCardProps) {
  const isPhaseActive = phase.value === carePhase || phase.selectedCareEnvironment !== null;
  const phaseLink = isPhaseActive ? getCareSettingsPageBy(phase.value) : null;
  const isCurrentPhase = phase.value === carePhase;

  const cardContent = (
    <Card className="border-border border-border group-hover:border-primary relative gap-0 border-2 p-0 shadow-none">
      <CardHeader className="gap-0 px-4 pb-0 pt-3">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <CardTitle className="text-base md:text-lg">{phase.carePhase}</CardTitle>
            <SlideOutSheet
              slideOutContent={phase.slideOutContent}
              title="FAQ"
            >
              <CircleQuestionMark
                className="text-muted-foreground hover:text-foreground relative z-20 size-4"
                aria-hidden="true"
              />
            </SlideOutSheet>
          </div>

          <div className="flex items-center gap-2">
            <Tooltip>
              <TooltipTrigger className="relative z-20">
                <Badge>{timelineText}</Badge>
              </TooltipTrigger>
              <TooltipContent>{timelineTooltip}</TooltipContent>
            </Tooltip>
          </div>
        </div>
      </CardHeader>

      <CardContent className="px-4 pb-4 pt-3">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {/* ADL description */}
          <div className="flex items-start gap-2">
            <span className="text-muted-foreground">{phase.adlRangeIcon}</span>
            <div>
              <p className="text-muted-foreground text-sm">Assisted Care needs</p>
              <p className="text-sm font-medium text-gray-950">
                {phase.adlRange} self-care activities
              </p>
            </div>
          </div>

          {/* Duration */}
          <div className="flex items-start gap-2">
            <Clock4 className="text-muted-foreground size-4" />
            <div>
              <p className="text-muted-foreground text-sm">
                {clientHasStartedLtc && phase.value === clientCurrentCarePhase
                  ? 'Remaining duration'
                  : 'Est. duration'}
              </p>
              <p className="text-sm font-medium text-gray-950">
                {phase.phaseCareMonthsNeeded} months
                {phase.isDurationCustom && (
                  <Badge
                    variant="secondary"
                    className="ml-1 text-xs"
                  >
                    Edited
                  </Badge>
                )}
              </p>
            </div>
          </div>

          {/* Care setting */}
          <div className="flex items-start gap-2">
            <MapPinIcon className="text-muted-foreground size-4" />
            <div>
              <p className="text-muted-foreground text-sm">Place of care</p>
              <p className="text-sm font-medium text-gray-950">
                {phase.selectedCareEnvironment
                  ? careEnvironmentDefs[phase.selectedCareEnvironment].label
                  : 'Not selected'}
              </p>
            </div>
          </div>

          {/* Cost - always visible */}
          <div className="flex items-start gap-2">
            <DollarSign className="text-muted-foreground size-4" />
            <div>
              <p className="text-muted-foreground text-sm">Est. cost</p>
              <p className="text-sm font-medium text-gray-950">
                {phase.selectedCareEnvironment
                  ? formatCurrency(phase.phaseInflatedProfessionalShareCost)
                  : '---'}
              </p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="group relative flex">
      {/* Left Column: Icon with number badge + connecting line */}
      <div className="relative mr-4 flex flex-col items-center md:mr-6">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={isVisible ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.9 }}
          transition={{
            duration: 0.2,
            type: 'spring',
            stiffness: 250,
            damping: 15,
          }}
          className="relative"
        >
          <div className="bg-background border-border group-hover:border-primary flex size-12 items-center justify-center rounded-xl border-2 md:size-14">
            {phase.adlRangeIcon}
          </div>
          <div className="border-background bg-primary absolute -right-2 -top-2 flex size-6 items-center justify-center rounded-full border-2 text-xs font-semibold text-white">
            {phaseNumber}
          </div>
        </motion.div>

        {/* Connecting line (not on last item) */}
        {!isLastPhase && (
          <motion.div
            initial={{ scaleY: 0 }}
            animate={isLineVisible ? { scaleY: 1 } : { scaleY: 0 }}
            transition={{ duration: 0.15, ease: 'easeOut' }}
            className="bg-accent min-h-6 w-0.5 flex-1 origin-top"
          />
        )}
      </div>

      {/* Right Column: Content card */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={isVisible ? { opacity: 1 } : { opacity: 0 }}
        transition={{ duration: 0.3, delay: 0.15 }}
        className={cn('flex-1', !isLastPhase && 'pb-6')}
      >
        <div className="group relative">
          {phaseLink && (
            <PageLink
              to={phaseLink}
              targetProps={{ clientId }}
              className="absolute z-10 block size-full"
            />
          )}
          {cardContent}
        </div>
      </motion.div>
    </div>
  );
}
